import LBox from "@components/LBox";
import LText from "@components/LText";
import UserAvatar from "@components/UserAvatar";
import LIcon from "@components/LIcon";
import LPressable from "@components/LPressable";
import { fonts, scaleFont } from "@theme";
import { StyleSheet } from "react-native";
import { router } from "expo-router";

export interface ProfileHeaderProps {
  userDisplayName: string;
  userAvatarUrl?: string;
  defaultAvatarColors?: [string, string] | null;
  editable?: boolean;
  onShare?: () => void | Promise<void>;
  isSharePending?: boolean;
}

export const ProfileHeader = ({
  userDisplayName,
  userAvatarUrl,
  defaultAvatarColors,
  editable = true,
  onShare,
  isSharePending = false,
}: ProfileHeaderProps) => {
  const avatar = (
    <>
      <UserAvatar
        image={userAvatarUrl}
        defaultAvatarColors={defaultAvatarColors}
        size={56}
        borderRadius="m-l"
      />
      {editable ? (
        <LBox
          width={18}
          height={18}
          borderRadius="max"
          backgroundColor="blackTranslucent40"
          alignItems="center"
          justifyContent="center"
          style={styles.editBadge}
        >
          <LIcon name="pencil" fill="transparent" size={11} />
        </LBox>
      ) : null}
    </>
  );

  return (
    <LBox
      flexDirection="row"
      alignItems="center"
      justifyContent="space-between"
      minHeight={64}
    >
      {editable ? (
        <LPressable
          onPress={() => router.push("/(app)/(tabs)/profile/edit")}
          sentryEventName="you_edit_profile_pressed"
        >
          {avatar}
        </LPressable>
      ) : (
        <LBox>{avatar}</LBox>
      )}

      <LBox flex={1} alignItems="flex-start" justifyContent="center" px="s3">
        <LText
          color="textPrimary"
          numberOfLines={1}
          adjustsFontSizeToFit
          style={styles.name}
        >
          {userDisplayName}
        </LText>
      </LBox>

      <LBox width={56} alignItems="flex-end">
        {onShare ? (
          <LPressable
            accessibilityLabel="Share"
            accessibilityRole="button"
            disabled={isSharePending}
            onPress={onShare}
            sentryEventName="you_share_pressed"
            style={StyleSheet.flatten([
              styles.shareButton,
              isSharePending ? styles.shareButtonDisabled : undefined,
            ])}
          >
            <LIcon
              colors={{ color: "#FFFFFF99" }}
              name="share"
              size={20}
            />
          </LPressable>
        ) : (
          <LBox
            accessibilityElementsHidden
            importantForAccessibility="no-hide-descendants"
            height={40}
            width={40}
          />
        )}
      </LBox>
    </LBox>
  );
};

const styles = StyleSheet.create({
  editBadge: {
    position: "absolute",
    bottom: 2,
    right: 2,
  },
  name: {
    fontFamily: fonts.semiBold,
    fontSize: scaleFont(24),
    lineHeight: scaleFont(30),
    textAlign: "left",
  },
  shareButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "rgba(255, 255, 255, 0.08)",
    borderWidth: 1,
    borderColor: "rgba(255, 255, 255, 0.08)",
    alignItems: "center",
    justifyContent: "center",
  },
  shareButtonDisabled: {
    opacity: 0.5,
  },
});

export default ProfileHeader;
